<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Loader2, X, Building2, UserCircle, AlertCircle } from 'lucide-vue-next'

import { API, HOST } from '@/utils/http'
import type { AdminOrganization } from '@/types/admin'

const emit = defineEmits<{
  (e: 'customer-created'): void
}>()

const open = ref(false)

const organizations = ref<AdminOrganization[]>([])
const loadingOrganizations = ref(false)
const selectedOrganizationId = ref<string>('')


const adminContact = ref<{ found: boolean; display_name?: string; first_name?: string; last_name?: string; email?: string; reason?: string } | null>(null)
const loadingContact = ref(false)

const creating = ref(false)
const error = ref<string | null>(null)

const filterOrganizations = (list: AdminOrganization[], search: string) => {
  const term = search.toLowerCase()
  return list.filter(org =>
    (org.organization_name?.toLowerCase().includes(term)) ||
    (String(org.organization_id).includes(term))
  )
}

const selectedOrganization = computed(() =>
  organizations.value.find(org => org.organization_id === parseInt(selectedOrganizationId.value))
)

const isFormValid = computed(() =>
  !!selectedOrganizationId.value && adminContact.value?.found === true
)

const fetchOrganizations = async () => {
  loadingOrganizations.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/organizations` })
    // Only show orgs not yet enrolled
    organizations.value = (response.data || []).filter((org: AdminOrganization) => !org.bridge_enrolled)
  } catch (err: any) {
    console.error('Error fetching organizations:', err)
  } finally {
    loadingOrganizations.value = false
  }
}

const fetchAdminContact = async (orgId: string) => {
  if (!orgId) { adminContact.value = null; return }
  loadingContact.value = true
  adminContact.value = null
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/organizations/${orgId}/admin-contact` })
    adminContact.value = response.data
  } catch (err: any) {
    adminContact.value = { found: false, reason: 'Could not fetch admin contact' }
  } finally {
    loadingContact.value = false
  }
}

watch(selectedOrganizationId, fetchAdminContact)

const resetForm = () => {
  selectedOrganizationId.value = ''
  adminContact.value = null
  error.value = null
}

const enroll = async () => {
  error.value = null
  creating.value = true
  try {
    await API({
      method: 'POST',
      url: `${HOST}/payments/bridge/organizations/${selectedOrganizationId.value}/enroll`
    })
    emit('customer-created')
    open.value = false
    resetForm()
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to enroll organization'
  } finally {
    creating.value = false
  }
}

fetchOrganizations()
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <Button>
        Enroll Business
      </Button>
    </DialogTrigger>
    <DialogContent class="sm:max-w-[460px]">
      <DialogHeader>
        <DialogTitle>Enroll Business</DialogTitle>
        <DialogDescription>
          Select an organization to enroll in Bridge payments. The org admin will be used as the primary contact.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-2">
        <div v-if="error" class="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm">
          {{ error }}
        </div>

        <!-- Org picker -->
        <div v-if="!selectedOrganizationId">
          <Command :filter-function="filterOrganizations">
            <CommandInput placeholder="Search organizations by name or ID..." :disabled="loadingOrganizations" />
            <CommandList class="max-h-[calc(10*2.25rem)] overflow-y-auto border rounded-md mt-1">
              <CommandEmpty>{{ loadingOrganizations ? 'Loading...' : 'No organization found.' }}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  v-for="org in organizations"
                  :key="org.organization_id"
                  :value="org"
                  @select="() => { selectedOrganizationId = String(org.organization_id) }"
                >
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <Building2 class="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span class="font-medium truncate">{{ org.organization_name }}</span>
                    <span class="text-xs text-muted-foreground flex-shrink-0">#{{ org.organization_id }}</span>
                    <span v-if="org.current_plan" class="text-xs text-primary flex-shrink-0">{{ org.current_plan }}</span>
                  </div>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
        <div v-else class="flex items-center gap-2 px-3 py-2 border rounded-md">
          <Building2 class="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span class="font-medium flex-1 truncate">{{ selectedOrganization?.organization_name }}</span>
          <span class="text-xs text-muted-foreground">#{{ selectedOrganization?.organization_id }}</span>
          <Button variant="ghost" size="icon" class="h-6 w-6 ml-1" @click="selectedOrganizationId = ''">
            <X class="h-3 w-3" />
          </Button>
        </div>

        <!-- Admin contact preview -->
        <div v-if="selectedOrganizationId">
          <label class="text-sm font-medium mb-1.5 block">Primary Contact</label>
          <div v-if="loadingContact" class="flex items-center gap-2 text-sm text-muted-foreground px-1">
            <Loader2 class="h-4 w-4 animate-spin" />
            Looking up org admin...
          </div>
          <div v-else-if="adminContact?.found" class="flex items-center gap-3 p-3 bg-muted rounded-md">
            <UserCircle class="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div class="text-sm">
              <div class="font-medium">{{ adminContact.display_name || `${adminContact.first_name} ${adminContact.last_name}`.trim() }}</div>
              <div class="text-muted-foreground">{{ adminContact.email }}</div>
            </div>
          </div>
          <div v-else-if="adminContact && !adminContact.found" class="flex items-center gap-2 p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800/50 rounded-md text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle class="h-4 w-4 flex-shrink-0" />
            {{ adminContact.reason }}
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <Button variant="outline" @click="open = false" :disabled="creating">Cancel</Button>
        <Button @click="enroll" :disabled="creating || !isFormValid">
          <Loader2 v-if="creating" class="mr-2 h-4 w-4 animate-spin" />
          Enroll
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
